/** Runtime configuration, read from Vite env. All optional. */

/**
 * API base URL.
 *
 * Set to `same-origin` to call the API through the page's own origin (the dev
 * server proxies /api → the WinterMute server). That is what makes phone testing
 * work behind a single HTTPS tunnel: no mixed content, no CORS, one URL.
 * Otherwise give an absolute URL, or leave unset for offline single-node mode.
 */
const RAW_API = (import.meta.env.VITE_API_URL as string | undefined)?.trim();

export const API_URL: string | null =
  RAW_API === 'same-origin' ? '' : RAW_API ? RAW_API.replace(/\/$/, '') : null;

export const MEDIAPIPE_WASM: string =
  (import.meta.env.VITE_MEDIAPIPE_WASM as string | undefined) ??
  'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.18/wasm';

export const FACE_MODEL: string =
  (import.meta.env.VITE_FACE_MODEL as string | undefined) ??
  'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task';

/** Whether the client has a backend for exchange + LLM mutation. */
export const HAS_API = API_URL !== null;

/**
 * Optional shared secret for uploading SLAM datasets, matching the server's
 * DATASET_UPLOAD_TOKEN. Leave unset for local/pilot use.
 */
export const UPLOAD_TOKEN: string =
  (import.meta.env.VITE_UPLOAD_TOKEN as string | undefined) ?? '';

/**
 * Spatial backend selection, best first:
 *   'auto'        — WebXR if the device offers immersive-ar, else AlvaAR if
 *                   vendored, else VO. Chosen by FEATURE DETECTION, never by
 *                   user-agent: if iOS Safari ever ships immersive-ar it is
 *                   picked up with no code change.
 *   'webxr'       — force native AR (Android Chrome today). Metric scale,
 *                   gravity-aligned floor, real hit-testing.
 *   'alva'        — force AlvaAR WASM SLAM (the only option on iOS today)
 *   'vo'          — camera-motion VO: dead reckoning, no map. Diagnostic only.
 *   'orientation' — rotation-only shell (safe fallback)
 */
export type SpatialMode = 'auto' | 'webxr' | 'alva' | 'vo' | 'orientation';

export const SPATIAL_MODE: SpatialMode =
  (import.meta.env.VITE_SPATIAL_MODE as SpatialMode | undefined) ?? 'auto';

/** VLM "watcher" configuration. Requires the API and separate consent. */
export const VLM = {
  /** ms between watcher observations while active. */
  intervalMs: 22000,
  /** Longest image edge sent to the vision model (px). */
  maxEdge: 1280,
  /** JPEG quality for the sent frame. */
  quality: 0.72,
};

/**
 * Icons (Iconostasis) gated off 2026-08-24: the point-cloud figures read as
 * flat, pasted-on billboards rather than objects standing in the room —
 * expected, since they are unsegmented gl.POINTS with no surface-fitting, and
 * that groundwork (bounding-box → map-coordinate grounding, real anchoring)
 * isn't done yet. Off until the icon rendering itself is reworked, not just
 * the anchoring under it. See docs/STATUS.md.
 */
export const ICONS_ENABLED = false;

/** Field/session tuning that is presentation-side (not orchestrator logic). */
export const FIELD = {
  /** Horizontal field of view in degrees mapped across the screen width. */
  fovDeg: 66,
  /** Angular tolerance (deg from center) for a fragment to count as "held". */
  holdToleranceDeg: 13,
  /** ms between orchestrator ticks (surfacing/decay). */
  tickMs: 1200,
  /** ms between mutation passes. */
  mutateMs: 4500,
};
